import { useEffect, useState } from "react";
import { api, HEADS, HEAD_COLORS } from "@/lib/api";
import { useWeek } from "@/context/WeekContext";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Legend, BarChart, Bar } from "recharts";
import { TrendingUp } from "lucide-react";

export default function Trends() {
  const { week } = useWeek();
  const [worsening, setWorsening] = useState([]);
  const [weeklyTotals, setWeeklyTotals] = useState([]);
  const [yearly, setYearly] = useState([]);

  useEffect(() => {
    (async () => {
      const [w, weeks, yr] = await Promise.all([
        api.get("/top-worsening", { params: { week_label: week, limit: 15 } }),
        api.get("/weeks"),
        api.get("/yearly-totals"),
      ]);
      setWorsening(w.data.rows || []);
      setYearly(yr.data.years || []);
      // fetch pair-aggregates per week for weekly totals
      const wks = (weeks.data || []).slice(0, 8).reverse();
      const totals = await Promise.all(wks.map(async (wk) => {
        const s = await api.get("/summary", { params: { week_label: wk } });
        return { week: wk, total: s.data.total_complaints, ...s.data.head_totals };
      }));
      setWeeklyTotals(totals);
    })();
  }, [week]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Week-over-Week Trends</h1>
        <p className="text-sm text-slate-500 mt-1">Historical complaint volume and the pairs getting worse.</p>
      </div>

      <Card className="p-4 border border-slate-200">
        <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-3">Weekly complaint volume (last 8 weeks)</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weeklyTotals}>
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {HEADS.map((h) => (
                <Line key={h} type="monotone" dataKey={h} stroke={HEAD_COLORS[h].stroke} strokeWidth={2} dot={{ r: 2 }} />
              ))}
              <Line type="monotone" dataKey="total" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 border border-slate-200">
        <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-3">Year-over-Year totals per head</div>
        {yearly.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No yearly data yet — upload weeks from different years to compare.</div>}
        {yearly.length > 0 && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearly}>
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {HEADS.map((h) => (
                  <Bar key={h} dataKey={h} stackId="y" fill={HEAD_COLORS[h].stroke} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-red-600" />
          <div className="text-xs uppercase font-semibold tracking-wider text-slate-700">Top Worsening Pairs (WoW)</div>
        </div>
        <div className="p-4">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={worsening} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="pair_code" tick={{ fontSize: 10 }} width={100} />
                <RTooltip />
                <Bar dataKey="total_delta" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-[11px] uppercase tracking-wider text-slate-500 border-t border-slate-100">
              <th className="text-left px-4 py-2">Pair</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-right px-4 py-2">Current</th>
              <th className="text-right px-4 py-2">Previous</th>
              <th className="text-right px-4 py-2">Δ</th>
            </tr>
          </thead>
          <tbody>
            {worsening.map((r) => (
              <tr key={r.pair_code} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono font-semibold">{r.pair_code}</td>
                <td className="px-4 py-2 text-slate-700">{r.pair_name}</td>
                <td className="px-4 py-2 text-right font-mono">{r.total}</td>
                <td className="px-4 py-2 text-right font-mono text-slate-500">{r.prev_total}</td>
                <td className="px-4 py-2 text-right font-mono font-bold text-red-600">+{r.total_delta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
